import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/context/i18n";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CheckCircle2, Loader2, Briefcase, Landmark, Car, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BANK } from "@/lib/bank";

export default function Confirmation() {
  const { reference } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { formatPrice } = useApp();
  const { t } = useI18n();
  const [booking, setBooking] = useState(location.state?.booking || null);
  const [loading, setLoading] = useState(!location.state?.booking);

  useEffect(() => {
    if (booking) return;
    api.get(`/bookings/${reference}`).then((r) => setBooking(r.data)).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [reference]);

  if (loading) return <div className="min-h-screen bg-[#F8FAFC]"><Header /><div className="pt-40 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#009E9E]" /></div></div>;
  if (!booking) return <div className="min-h-screen bg-[#F8FAFC]"><Header /><div className="pt-40 text-center text-[#475569]">{t("booking_not_found")}</div></div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="pt-28 max-w-2xl mx-auto px-4 lg:px-8 fade-up">
        <div className="text-center mb-8">
          <CheckCircle2 className="w-16 h-16 text-[#009E9E] mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="font-display text-4xl font-bold text-[#0B2038]">{t("all_set")}</h1>
          <p className="text-[#475569] mt-2">{t("confirmation_sent")} <span className="font-bold text-[#0B2038]" data-testid="booking-reference">{booking.reference}</span></p>
        </div>

        {booking.paymentMethod === "wire" && booking.status === "PENDING" && (
          <div data-testid="wire-pending-banner" className="bg-[#FFFBEB] border border-amber-200 rounded-2xl p-5 mb-6">
            <p className="font-semibold text-amber-800 flex items-center gap-2"><Landmark className="w-4 h-4" />{t("awaiting_transfer")}</p>
            <p className="text-sm text-amber-700 mt-1">{t("pending_note")}</p>
            <div className="grid gap-1.5 mt-3 pt-3 border-t border-amber-200 text-sm">
              <div className="flex justify-between gap-3"><span className="text-amber-700">{t("account_name")}</span><span className="font-semibold text-[#0B2038] text-right">{BANK.account}</span></div>
              <div className="flex justify-between gap-3"><span className="text-amber-700">{t("bank_name")}</span><span className="font-semibold text-[#0B2038] text-right">{BANK.bank}</span></div>
              <div className="flex justify-between gap-3"><span className="text-amber-700">IBAN</span><span className="font-mono font-semibold text-[#0B2038]">{BANK.iban}</span></div>
              <div className="flex justify-between gap-3"><span className="text-amber-700">SWIFT</span><span className="font-mono font-semibold text-[#0B2038]">{BANK.swift}</span></div>
              <div className="flex justify-between gap-3"><span className="text-amber-700">{t("ref")}</span><span className="font-mono font-bold text-[#0B2038]">{booking.reference}</span></div>
            </div>
          </div>
        )}

        <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden" data-testid="confirmation-card">
          {booking.image && <div className="h-48"><img src={booking.image} alt="" className="w-full h-full object-cover" /></div>}
          <div className="p-6">
            <span className="label-caps">{booking.productType}</span>
            <h2 className="font-display text-2xl font-bold text-[#0B2038] mt-1">{booking.title}</h2>
            <p className="text-[#475569] mt-1">{booking.subtitle}</p>
            <div className="grid grid-cols-2 gap-4 mt-5 pt-5 border-t border-[#E2E8F0] text-sm">
              <div><p className="text-xs text-[#475569]">{t("status")}</p><p className={`font-semibold ${booking.status === "CONFIRMED" ? "text-green-600" : booking.status === "PENDING" ? "text-amber-600" : "text-[#0B2038]"}`} data-testid="booking-status">{booking.status}</p></div>
              <div><p className="text-xs text-[#475569]">{booking.status === "PENDING" ? t("amount") : t("total_paid")}</p><p className="font-display text-xl font-bold text-[#0B2038]">{formatPrice(booking.priceEur)}</p></div>
              <div><p className="text-xs text-[#475569]">{t("lead_guest")}</p><p className="font-semibold text-[#0B2038]">{booking.holder?.name} {booking.holder?.surname}</p></div>
              {booking.providerReference && <div><p className="text-xs text-[#475569]">{t("provider_ref")}</p><p className="font-semibold text-[#0B2038]">{booking.providerReference}</p></div>}
            </div>
          </div>
        </div>

        {booking.productType === "hotel" && (
          <div data-testid="transfer-upsell" className="mt-6 bg-white border border-[#E2E8F0] rounded-2xl p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#F0FDFA] flex items-center justify-center shrink-0"><Car className="w-6 h-6 text-[#009E9E]" /></div>
            <div className="min-w-0 flex-1">
              <p className="font-display text-lg font-bold text-[#0B2038] leading-tight">{t("add_transfer_title")}</p>
              <p className="text-sm text-[#475569] mt-0.5">{t("add_transfer_sub")}</p>
            </div>
            <Button data-testid="add-transfer-btn"
              onClick={() => navigate(`/results/transfers?${new URLSearchParams({ destination: "AYT", airportId: "ayt", direction: "arrival", date: booking.meta?.checkIn || "", adults: 2 }).toString()}`)}
              className="rounded-full bg-[#009E9E] hover:bg-[#007A7A] text-white font-semibold shrink-0">
              {t("add_transfer_cta")} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        )}

        <div className="flex gap-3 mt-6">
          <Link to="/trips" className="flex-1"><Button data-testid="view-trips-button" className="w-full h-12 rounded-full bg-[#0B2038] text-white font-semibold"><Briefcase className="w-4 h-4 mr-2" />{t("trips")}</Button></Link>
          <Link to="/" className="flex-1"><Button variant="outline" className="w-full h-12 rounded-full border-[#0B2038] text-[#0B2038] font-semibold">{t("book_more")}</Button></Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
